/**
 * Token storage.
 *
 * Kept behind an interface so the native build can move to Keychain/Keystore
 * without touching callers. The in-memory copy avoids an async read on every
 * request; persistence keeps the donor signed in across launches.
 */

import type { AuthTokens } from '@mfa/domain';

import { storage } from '../storage/storage';

const ACCESS_KEY = 'mfa.auth.access';
const REFRESH_KEY = 'mfa.auth.refresh';

let cachedAccess: string | null = null;
let cachedRefresh: string | null = null;
let hydrated = false;

async function hydrate(): Promise<void> {
  if (hydrated) return;
  cachedAccess = await storage.getItem(ACCESS_KEY);
  cachedRefresh = await storage.getItem(REFRESH_KEY);
  hydrated = true;
}

export const tokenStore = {
  async getAccessToken(): Promise<string | null> {
    await hydrate();
    return cachedAccess;
  },

  async getRefreshToken(): Promise<string | null> {
    await hydrate();
    return cachedRefresh;
  },

  async save(tokens: Pick<AuthTokens, 'accessToken' | 'refreshToken'>): Promise<void> {
    cachedAccess = tokens.accessToken;
    cachedRefresh = tokens.refreshToken;
    hydrated = true;
    await Promise.all([
      storage.setItem(ACCESS_KEY, tokens.accessToken),
      storage.setItem(REFRESH_KEY, tokens.refreshToken),
    ]);
  },

  async clear(): Promise<void> {
    cachedAccess = null;
    cachedRefresh = null;
    hydrated = true;
    await Promise.all([storage.removeItem(ACCESS_KEY), storage.removeItem(REFRESH_KEY)]);
  },

  async isSignedIn(): Promise<boolean> {
    await hydrate();
    return cachedAccess !== null;
  },

  /** Test seam: forces the next read to go back to storage. */
  _resetForTests(): void {
    cachedAccess = null;
    cachedRefresh = null;
    hydrated = false;
  },
};
